import React, { lazy, Suspense, useEffect, useState } from 'react'
import { useNavigate } from 'react-router-dom'
import { toast } from 'react-toastify'
import { Box, Step, StepLabel, Stepper } from '@mui/material'
import { useQueryClient } from '@tanstack/react-query'
import { useFormik } from 'formik'
import * as yup from 'yup'

import OrgAPI from '../../../api/orbitV1/OrgAPI'
import UenAPI from '../../../api/orbitV1/UenAPI'
import UserAPI from '../../../api/orbitV1/UserAPI'
import PageLayout from '../../../common/Layout/Page/PageLayout'
import useDebounce from '../../../hooks/useDebounce'
import { AddUser } from '../components'

const CompanyDetails = lazy(
  () => import('../../Onboarding/components/CompanyDetails')
)
const ContactDetails = lazy(
  () => import('../../Onboarding/components/ContactDetails')
)
const UserAccount = lazy(() => import('../components/UserAccount'))

export default function AddCustomer() {
  /* Page Variables */
  const [page, setPage] = useState(0)
  const [customerDetails, setCustomerDetails] = useState({})
  const [isAddUserModalOpen, setIsAddUserModalOpen] = useState(false)
  const [isEditUserModalOpen, setIsEditUserModalOpen] = useState(false)
  const [userAccountDetails, setUserAccountDetails] = useState({})
  const [currOrgId, setCurrOrgId] = useState('')
  const roleOptions = ['owner', 'admin']
  const navigate = useNavigate()
  const queryClient = useQueryClient()

  /* Company Details Variables and error states */
  const [orgNameSearchResults, setOrgNameSearchResults] = useState([])
  const [isOrgSearching, setIsOrgSearching] = useState(false)
  const [isAddrSearching, setIsAddrSearching] = useState(false)

  /* Delivery Address Variables and error states */
  const [deliveryAddrSearch, setDeliveryAddrSearch] = useState('')
  const [deliveryAddrSearchResults, setDeliveryAddrSearchResults] = useState([])

  /* Billing Address Variables and error states */
  const [billingAddrSearch, setBillingAddrSearch] = useState('')
  const [billingAddrSearchResults, setBillingAddrSearchResults] = useState([])
  const [isBillingAddrSame, setIsBillingAddrSame] = useState(false)

  /* Formik variables */
  const validationSchema = yup.object({
    contactName: yup.string().required('Contact Name is required'),
    contactNumber: yup
      .string()
      .matches(/^([689])\d{7}$/, 'Invalid Contact Number')
      .required('Contact Number is required'),
    email: yup
      .string()
      .email('Enter a valid email')
      .required('Email is required'),
    companyName: yup.string().required('Company name is required'),
    uen: yup
      .string()
      .matches(
        /^(S|T)([\d]{2})([A-Z]{2})([\d]{4})([A-Z])$|^[0-9]{9}[A-Z]$|^[0-9]{8}[A-Z]$/g,
/*
        /^\d{8}[A-Z]$/ || /^\d{9}[A-Z]$/ || /^[A-Z]\d{2}[A-Z]{2}\d{4}[A-Z]$/,
*/
        'Invalid UEN'
      )
      .required('UEN is required'),
    role: yup.string(),
    deliveryAddress: yup
      .object()
      .shape({
        blockNo: yup.string(),
        streetName: yup.string(),
        buildingName: yup.string(),
        postalCode: yup.string().matches(/^\d{6}$/, 'Invalid Postal Code'),
        unitNo: yup.string().required('Unit number is required')
      })
      .required('Delivery address is required'),
    billingAddress: yup.object().shape({
      blockNo: yup.string(),
      streetName: yup.string(),
      buildingName: yup.string(),
      postalCode: yup.string(),
      unitNo: yup.string()
    })
  })

  const formik = useFormik({
    initialValues: {
      contactName: '',
      contactNumber: '',
      email: '',
      companyName: '',
      uen: '',
      deliveryAddress: {
        blockNo: '',
        streetName: '',
        buildingName: '',
        postalCode: '',
        unitNo: ''
      },
      billingAddress: {
        blockNo: '',
        streetName: '',
        buildingName: '',
        postalCode: '',
        unitNo: ''
      }
    },
    validationSchema,
    onSubmit: (values) => {
      toast.error(JSON.stringify(values))
    }
  })

  const addUserFormik = useFormik({
    initialValues: {
      contactName: '',
      email: '',
      contactNumber: '',
      role: ''
    },
    validationSchema,
    onSubmit: (values) => {
      toast.error(JSON.stringify(values))
    }
  })

  const editUserFormik = useFormik({
    initialValues: {
      userId: '',
      orgId: '',
      contactName: '',
      email: '',
      contactNumber: '',
      role: ''
    },
    validationSchema,
    onSubmit: (values) => {
      toast.error(JSON.stringify(values))
    }
  })

  /* useDebounce hook */
  const debouncedCompanyNameSearch = useDebounce(
    formik.values.companyName,
    1000
  )
  const debouncedUENSearch = useDebounce(
    formik.values.uen,
    1000
  )
  const debouncedDeliveryAddressSearch = useDebounce(deliveryAddrSearch, 1000)
  const debouncedBillingAddressSearch = useDebounce(billingAddrSearch, 1000)

  /* function to search for company name */
  const handleCompanyNameSearch = async (search) => {
    try {
      const companySearch = await UenAPI.companyNameSearch(search)
      if (companySearch) {
        setIsOrgSearching(false)
        setOrgNameSearchResults(companySearch.data)
      }
    } catch (error) {
      toast.error('Error retrieving company name')
    }
  }

  /* function to search for company name */
  const handleUENSearch = async (search) => {
    try {
      const companySearch = await UenAPI.companyUENSearch(search)
      if (companySearch) {
        setIsOrgSearching(false)
        setOrgNameSearchResults(companySearch.data)
      }
    } catch (error) {
      toast.error('Error retrieving uen')
    }
  }

  /* function to search for delivery address */
  const handleDeliveryAddressSearch = async (deliverySearch) => {
    try {
      const deliveryAddressSearch = await OrgAPI.getAddress(deliverySearch)
      if (deliveryAddressSearch) {
        setIsAddrSearching(false)
        setDeliveryAddrSearchResults(deliveryAddressSearch.data)
      }
    } catch (error) {
      toast.error('Error fetching address')
    }
  }

  /* function to search for billing address */
  const handleBillingAddressSearch = async (billingSearch) => {
    try {
      const billingAddressSearch = await OrgAPI.getAddress(billingSearch)
      if (billingAddressSearch) {
        setIsAddrSearching(false)
        setBillingAddrSearchResults(billingAddressSearch.data)
      }
    } catch (error) {
      toast.error('Error fetching address')
    }
  }

  /* Debounce searches */
  useEffect(() => {
    if (debouncedDeliveryAddressSearch) {
      setIsAddrSearching(true)
      handleDeliveryAddressSearch(debouncedDeliveryAddressSearch)
    }

    if (debouncedBillingAddressSearch) {
      setIsAddrSearching(true)
      handleBillingAddressSearch(debouncedBillingAddressSearch)
    }

    if (debouncedCompanyNameSearch) {
      setIsOrgSearching(true)
      handleCompanyNameSearch(debouncedCompanyNameSearch)
    }

    if (debouncedUENSearch) {
      setIsOrgSearching(true)
      handleUENSearch(debouncedUENSearch)
    }
  }, [
    debouncedDeliveryAddressSearch,
    debouncedBillingAddressSearch,
    debouncedCompanyNameSearch,
    debouncedUENSearch
  ])

  const retrieveAddress = async (postal) => {
    try {
      const addressDetails = await OrgAPI.getAddress(postal)

      const addrDetails = addressDetails.data[0]
      formik.setFieldValue('deliveryAddress.blockNo', addrDetails.BLK_NO)
      formik.setFieldValue('deliveryAddress.streetName', addrDetails.ROAD_NAME)
      formik.setFieldValue(
        'deliveryAddress.buildingName',
        addrDetails.BUILDING ? addrDetails.BUILDING : ''
      )
      formik.setFieldValue('deliveryAddress.postalCode', addrDetails.POSTAL)
      formik.setFieldValue('billingAddress.blockNo', addrDetails.BLK_NO)
      formik.setFieldValue('billingAddress.streetName', addrDetails.ROAD_NAME)
      formik.setFieldValue(
        'billingAddress.buildingName',
        addrDetails.BUILDING ? addrDetails.BUILDING : ''
      )
      formik.setFieldValue('billingAddress.postalCode', addrDetails.POSTAL)
    } catch (error) {
      toast.error('Error retrieving address')
    }
  }

  useEffect(() => {
    if (userAccountDetails) {
      editUserFormik.setFieldValue('contactName', userAccountDetails.name)
      editUserFormik.setFieldValue('email', userAccountDetails.email)
      editUserFormik.setFieldValue(
        'contactNumber',
        userAccountDetails.contactNumber
      )
      editUserFormik.setFieldValue('userId', userAccountDetails._id)
      editUserFormik.setFieldValue('orgId', userAccountDetails.orgId)
      editUserFormik.setFieldValue('role', userAccountDetails.role)
    }
  }, [userAccountDetails])

  const goNextPage = () => setPage(page + 1)

  const goPreviousPage = () => setPage(page - 1)

  const handleCompanyDetailsBack = () => navigate(-1)

  const getOrgDetails = async (orgId) => {
    const newOrgDetails = await OrgAPI.getOrgDetails(orgId)
    setCustomerDetails(newOrgDetails.data[0])
  }

  const handleContactDetailsNext = async () => {
    if (!currOrgId) {
      /* Construct request body */
      const orgDetails = {
        uen: formik.values.uen,
        orgName: formik.values.companyName,
        contactNumber: formik.values.contactNumber,
        email: formik.values.email,
        contactName: formik.values.contactName,
        role: 'owner',
        deliveryAddress: formik.values.deliveryAddress,
        billingAddress: formik.values.billingAddress
      }

      try {
        const orgCreation = await OrgAPI.createOrg(orgDetails)
        if (orgCreation.data) {
          const orgId = orgCreation.data.orgId
          await getOrgDetails(orgId)
          setCurrOrgId(orgId)
          formik.resetForm()
          goNextPage()
        } else {
          goNextPage()
        }
      } catch (error) {
        toast.error('Unable to create customer. Please try again later')
      }
    }
  }

  const handleAddUser = async () => {
    const userDetails = {
      name: addUserFormik.values.contactName,
      contactNumber: addUserFormik.values.contactNumber,
      email: addUserFormik.values.email,
      role: addUserFormik.values.role,
      orgId: currOrgId
    }
    setIsAddUserModalOpen(false)
    try {
      const userCreation = await UserAPI.createUser(userDetails)

      if (userCreation.data) {
        getOrgDetails(currOrgId)
        addUserFormik.resetForm()
      }
    } catch (error) {
      toast.error('Unable to add user')
    }
  }

  const handleEditUser = async () => {
    const userDetails = {
      name: editUserFormik.values.contactName,
      email: editUserFormik.values.email,
      contactNumber: editUserFormik.values.contactNumber,
      role: editUserFormik.values.role,
      orgId: editUserFormik.values.orgId,
      userId: editUserFormik.values.userId
    }

    try {
      const res = await UserAPI.updateUser(userDetails)
      setIsEditUserModalOpen(false)

      if (res) {
        toast.success('User Account successfully updated.', {
          autoClose: 3000,
          hideProgressBar: false
        })
        getOrgDetails()
        editUserFormik.resetForm()
      }
    } catch (error) {
      toast.error('Error updating user. Please try again later!')
    }
  }

  const handleAddUserConfirm = async () => {
    queryClient.invalidateQueries({
      predicate: (query) =>
        query.queryKey[0] === 'getAllOrgsDetails' &&
        query.queryKey[1] === 'customerManagement'
    })
    navigate('/hq/manage-customers-orbit')
  }

  const COMPANY_DETAILS = (
    <CompanyDetails
      billingAddrSearch={billingAddrSearch}
      billingAddrSearchResults={billingAddrSearchResults}
      deliveryAddrSearch={deliveryAddrSearch}
      deliveryAddrSearchResults={deliveryAddrSearchResults}
      formik={formik}
      isAddrSearching={isAddrSearching}
      isBillingAddrSame={isBillingAddrSame}
      isOrgSearching={isOrgSearching}
      orgNameSearchResults={orgNameSearchResults}
      backHandler={handleCompanyDetailsBack}
      retrieveAddress={retrieveAddress}
      goPreviousPage={goPreviousPage}
      nextHandler={goNextPage}
      setBillingAddrSearch={setBillingAddrSearch}
      setBillingAddrSearchResults={setBillingAddrSearchResults}
      setDeliveryAddrSearch={setDeliveryAddrSearch}
      setDeliveryAddrSearchResults={setDeliveryAddrSearchResults}
      setIsBillingAddrSame={setIsBillingAddrSame}
    />
  )

  const CONTACT_DETAILS = (
    <ContactDetails
      formik={formik}
      nextHandler={handleContactDetailsNext}
      backHandler={goPreviousPage}
    />
  )

  const USER_ACCOUNTS = (
    <UserAccount
      customers={customerDetails}
      goPreviousPage={goPreviousPage}
      setIsAddUserModalOpen={setIsAddUserModalOpen}
      nextHandler={handleAddUserConfirm}
      backHandler={goPreviousPage}
      variant='add-new-customer'
      setUserAccountDetails={setUserAccountDetails}
      setIsEditUserModalOpen={setIsEditUserModalOpen}
    />
  )

  const steps = [
    {
      stepName: 'company-details',
      label: 'Company Details',
      content: COMPANY_DETAILS
    },
    {
      stepName: 'contact-details',
      label: 'Contact Details',
      content: CONTACT_DETAILS
    },
    {
      stepName: 'user-accounts',
      label: 'User Accounts',
      content: USER_ACCOUNTS
    }
  ]

  return (
    <PageLayout header='Add New Customer'>
      <Box sx={{ padding: '30px 0px' }}>
        <Stepper activeStep={page} alternativeLabel>
          {steps.map((step, index) => (
            <Step key={index}>
              <StepLabel>{step.label}</StepLabel>
            </Step>
          ))}
        </Stepper>
        <Suspense fallback={<div> Loading ... </div>}>
          {steps[page].content}
        </Suspense>
        {isAddUserModalOpen && (
          <AddUser
            isAddUserModalOpen={isAddUserModalOpen}
            setIsAddUserModalOpen={setIsAddUserModalOpen}
            roleOptions={roleOptions}
            handleAddUser={handleAddUser}
            formik={addUserFormik}
          />
        )}
        {isEditUserModalOpen && (
          <AddUser
            isAddUserModalOpen={isEditUserModalOpen}
            setIsAddUserModalOpen={setIsEditUserModalOpen}
            roleOptions={roleOptions}
            handleAddUser={handleEditUser}
            formik={editUserFormik}
          />
        )}
      </Box>
    </PageLayout>
  )
}
